import { supabase } from './supabase';
import { StorageService, type UploadProgress, type UploadResult } from './storage';
import { StorageLimitService } from './storage-limit-service';
import { AIAnalysisService, type AnalysisRequest, type AnalysisResult } from './ai-analysis';
import { ActivityService } from './activity';
import { EmailService } from './email-service';
import { LimitEnforcementService } from './limit-enforcement-service';
import type { Database } from './database.types';
import * as Sentry from '@sentry/react';

type Creative = Database['public']['Tables']['creatives']['Row'];
type CreativeInsert = Database['public']['Tables']['creatives']['Insert'];
type CreativeUpdate = Database['public']['Tables']['creatives']['Update'];

export interface CreativeWithAnalysis extends Creative {
  analysis?: AnalysisResult;
  projectName?: string;
}

// Helper to get display name for a creative family
export const getCreativeFamilyName = (creative: CreativeWithAnalysis): string => {
  return creative.family_name || creative.name;
};

export interface CreateCreativeRequest {
  projectId?: string; // Made optional - creatives can be unassigned initially
  file: File;
  name?: string;
  platforms?: string[]; // Now optional as we analyze all platforms by default
  campaignType?: string;
  industry?: string;
  autoAnalyze?: boolean;
}

export interface CreativeProgress {
  stage: 'uploading' | 'analyzing' | 'saving' | 'completed' | 'error';
  progress: number;
  message: string;
  error?: string;
}

export class CreativeService {
  /**
   * Create a new creative with file upload and optional AI analysis
   */
  static async createCreative(
    request: CreateCreativeRequest,
    onProgress?: (progress: CreativeProgress) => void
  ): Promise<CreativeWithAnalysis> {
    try {
      console.log('📥 CreativeService: Starting createCreative method...');
      console.log('📁 File details:', {
        name: request.file.name,
        size: request.file.size,
        type: request.file.type
      });

      const { projectId, file, name, platforms, campaignType, industry, autoAnalyze = true } = request;

      console.log('📊 Destructured request details:', {
        projectId: projectId || 'unassigned',
        fileName: file.name,
        autoAnalyze,
        campaignType,
        industry
      });

      // Stage 1: Upload file to storage (0-30%)
      console.log('📤 Starting file upload to storage...');
      onProgress?.({
        stage: 'uploading',
        progress: 1,
        message: 'Starting upload...'
      });

      const uploadResult = await StorageService.uploadCreative(
        file,
        projectId,
        (uploadProgress) => {
          // Map storage upload progress from 1% to 30%
          const mappedProgress = Math.max(1, Math.min(30, Math.round(1 + (uploadProgress.percentage * 0.29))));
          console.log(`📤 Upload progress: ${uploadProgress.percentage}% -> ${mappedProgress}%`);
          onProgress?.({
            stage: 'uploading',
            progress: mappedProgress,
            message: `Uploading... ${mappedProgress}%`
          });
        }
      );

      console.log('✅ File uploaded successfully');
      console.log('📊 Upload result:', { url: uploadResult.url, size: uploadResult.fileSize });

      // Stage 2: Save to database (30-40%)
      onProgress?.({
        stage: 'saving',
        progress: 35,
        message: 'Saving creative to database...'
      });

      // Get the current user for ownership
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      console.log('👤 User authenticated:', user.id);

      // Save creative metadata to database with user ownership
      const creativeName = name || file.name;
      const creativeMetadata = {
        user_id: user.id, // Add user_id for RLS policies
        project_id: projectId || null, // Allow null for individual creatives
        name: creativeName,
        family_name: creativeName, // Set initial family name to the creative name
        type: file.type.startsWith('image/') ? 'image' : 'video',
        url: uploadResult.url,
        file_size: file.size,
        file_type: file.type,
        analysis_data: null,
        status: 'uploaded', // Use 'uploaded' status for all creatives
        version_number: 1, // Initial version number
        is_current_version: true, // Mark as current version
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: creative, error: insertError } = await supabase
        .from('creatives')
        .insert(creativeMetadata)
        .select()
        .single();

      if (insertError) {
        throw new Error(`Failed to save creative: ${insertError.message}`);
      }

      // Log creative upload activity - only if project exists
      if (projectId && creative) {
        try {
          await ActivityService.logCreativeUploaded(
            projectId,
            creative.id,
            creative.name
          );
          console.log('✅ Activity logged: Creative uploaded');
        } catch (activityError) {
          console.warn('⚠️ Failed to log activity:', activityError);
          // Don't fail the entire operation if activity logging fails
        }
      }

      onProgress?.({
        stage: 'saving',
        progress: 40,
        message: 'Creative saved successfully!'
      });

      // Stage 3: AI Analysis (if requested) - starts at 45%
      let analysisResult: AnalysisResult | undefined;
      let analysisResponse: any; // Store the full response including credit info
      
      // Always analyze all platforms by default when autoAnalyze is true
      if (autoAnalyze) {
        console.log('🤖 CreativeService: Starting AI analysis stage...');
        console.log('📊 AutoAnalyze flag:', autoAnalyze);
        
        onProgress?.({
          stage: 'analyzing',
          progress: 45,
          message: 'Starting AI analysis...'
        });


        const analysisRequest: AnalysisRequest = {
          creativeUrl: uploadResult.publicUrl,
          fileName: name || file.name,
          creativeType: uploadResult.mimeType.startsWith('image/') ? 'image' : 'video',
          mimeType: uploadResult.mimeType,
          campaignType,
          industry,
          videoDuration: uploadResult.videoDuration
        };

        console.log('📋 CreativeService: Analysis request prepared:', {
          creativeUrl: uploadResult.publicUrl,
          creativeType: analysisRequest.creativeType,
          campaignType,
          industry
        });

        console.log('🚀 CreativeService: About to call AIAnalysisService.analyzeCreative...');
        
        try {
          console.log('⏳ CreativeService: Calling AI analysis service...');
          analysisResponse = await AIAnalysisService.analyzeCreative(analysisRequest);
          console.log('✅ CreativeService: AI analysis call completed successfully');
          
          // Handle the new response format that includes credit information
          if (analysisResponse.analysis) {
            analysisResult = analysisResponse.analysis;
            console.log('📊 CreativeService: Analysis result received with ID:', analysisResult?.analysisId);
            console.log('💳 CreativeService: Credit info received:', {
              analysesUsed: analysisResponse.analysesUsed,
              analysesLimit: analysisResponse.analysesLimit
            });
          } else {
            // Fallback for older format (if analysisResponse is just the analysis)
            analysisResult = analysisResponse as AnalysisResult;
            console.log('📊 CreativeService: Analysis result received with ID:', analysisResult?.analysisId);
          }
          
          // IMPORTANT: Return the result immediately for navigation, don't wait for database save
          onProgress?.({
            stage: 'analyzing',
            progress: 85,
            message: 'AI analysis completed!'
          });
          
          console.log('📋 CreativeService: Progress updated to 85%');
          
          // The database save happens inside analyzeCreative method, but we'll return the result regardless
          console.log('🎉 CreativeService: Analysis complete, preparing to return result for navigation');
          
        } catch (analysisError) {
          console.error('❌ CreativeService: AI analysis failed:', analysisError);
          console.error('❌ Error type:', typeof analysisError);
          console.error('❌ Error details:', analysisError instanceof Error ? analysisError.message : String(analysisError));
          
          // Check if this is a database save timeout but analysis was actually completed
          if (analysisError instanceof Error && analysisError.message.includes('Database save timeout')) {
            console.log('⚠️ Database save timed out, but analysis may have completed successfully');
            
            // If we have an analysis result despite the timeout, use it
            if (analysisResult && analysisResult.analysisId) {
              console.log('✅ Analysis result available despite database timeout, continuing...');
              onProgress?.({
                stage: 'analyzing',
                progress: 85,
                message: 'Analysis completed (database save delayed)'
              });
            } else {
              throw analysisError;
            }
          } else {
            throw analysisError;
          }
        }
        
        console.log('🎉 CreativeService: Received analysis result!');
        console.log('📊 Analysis result type:', typeof analysisResult);
        console.log('📊 Analysis result keys:', analysisResult ? Object.keys(analysisResult) : 'null');

        // Analysis is automatically saved in analyzeCreative method (or attempted)
        // Also update the creative record with the analysis data for immediate frontend access
        onProgress?.({
          stage: 'saving',
          progress: 95,
          message: 'Saving analysis results...'
        });
        
        console.log('📋 CreativeService: Progress updated to 95%');

        // Update the creative record with analysis data for immediate access
        try {
          console.log('💾 CreativeService: Updating creative record with analysis data...');
          
          // Add timeout to prevent hanging
          const updatePromise = supabase
            .from('creatives')
            .update({ 
              analysis_data: analysisResult as any,
              status: 'analyzed'
            })
            .eq('id', creative.id);
            
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Creative update timeout after 10 seconds')), 10000)
          );
          
          const { error: updateError } = await Promise.race([updatePromise, timeoutPromise]) as any;

          if (updateError) {
            console.warn('Failed to update creative with analysis data:', updateError);
            // Don't fail the entire operation, analysis is already saved in analyses table
          } else {
            console.log('✅ Creative updated with analysis data');
          }
        } catch (updateError) {
          console.warn('Error updating creative with analysis data:', updateError);
          // Don't fail the entire operation - analysis is saved in analyses table
          // This ensures navigation still happens even if creative update fails
        }
        
        console.log('🎯 CreativeService: Analysis flow completed, sending completion progress...');
        
        // Send email notification for analysis completion
        if (analysisResult && user) {
          try {
            console.log('📧 Sending analysis complete email notification...');
            
            // Get user details
            const { data: userData } = await supabase
              .from('users')
              .select('name, email')
              .eq('id', user.id)
              .single();
            
            if (userData && userData.email) {
              // Use the correct overall scores from analysis result
              const complianceScore = Math.round(analysisResult.overallScore || 0);
              const performanceScore = Math.round(analysisResult.performanceAnalysis?.overallPerformanceScore || 0);
              
              // Use the real analysis ID from the analysis result
              const analysisId = analysisResult.analysisId || `analysis-${Date.now()}`;
              
              await EmailService.send({
                to: userData.email,
                template: 'analysis-complete',
                data: {
                  userName: userData.name || 'there',
                  creativeName: creativeName,
                  complianceScore: complianceScore,
                  performanceScore: performanceScore,
                  analysisId: analysisId
                },
                userId: user.id
              });
              
              console.log('✅ Analysis complete email sent successfully');
            }
          } catch (emailError) {
            console.warn('⚠️ Failed to send analysis complete email:', emailError);
            // Don't fail the operation if email fails
          }
        }
      }

      onProgress?.({
        stage: 'completed',
        progress: 100,
        message: 'Creative created successfully!'
      });
      
      console.log('📡 CreativeService: Completion progress sent');
      console.log('🎯 CreativeService: About to return final result');
      console.log('📊 Final creative ID:', creative.id);
      console.log('📊 Final analysis ID:', analysisResult?.analysisId);
      
      const finalResult = {
        ...creative,
        analysis: analysisResult,
        // Include credit information if available for immediate UI updates
        analysesUsed: analysisResponse?.analysesUsed,
        analysesLimit: analysisResponse?.analysesLimit
      };
      
      console.log('✅ CreativeService: Returning final result with keys:', Object.keys(finalResult));
      console.log('🏁 CreativeService: Final result analysis present:', !!finalResult.analysis);
      return finalResult;

    } catch (error) {
      console.error('Create creative error:', error);
      
      // Check if it's a storage limit error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isStorageError = errorMessage.toLowerCase().includes('storage limit');
      
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: isStorageError ? 'Storage limit exceeded' : 'Failed to create creative',
        error: errorMessage
      });
      throw error;
    }
  }

  /**
   * Get or create a default project for the user
   */
  static async getOrCreateDefaultProject(userId: string): Promise<string> {
    try {
      // First try to find an existing "My Analyses" project
      const { data: existingProject, error: searchError } = await supabase
        .from('projects')
        .select('id')
        .eq('user_id', userId)
        .eq('name', 'My Analyses')
        .limit(1)
        .single();

      if (existingProject && !searchError) {
        console.log('✅ Found existing "My Analyses" project:', existingProject.id);
        return existingProject.id;
      }

      // If no "My Analyses" project exists, create one
      console.log('📁 Creating new "My Analyses" project...');
      const { data: newProject, error: createError } = await supabase
        .from('projects')
        .insert({
          user_id: userId,
          name: 'My Analyses',
          description: 'Default project for unassigned analyses',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (createError) {
        throw new Error(`Failed to create default project: ${createError.message}`);
      }

      console.log('✅ Created new "My Analyses" project:', newProject.id);
      return newProject.id;
    } catch (error) {
      console.error('❌ Error getting/creating default project:', error);
      throw error;
    }
  }

  /**
   * Get creative by ID with analysis data
   */
  static async getCreative(creativeId: string): Promise<CreativeWithAnalysis | null> {
    try {
      const { data: creative, error } = await supabase
        .from('creatives')
        .select(`
          *,
          projects(name)
        `)
        .eq('id', creativeId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }

      return {
        ...creative,
        analysis: creative.analysis_data as unknown as AnalysisResult | undefined,
        projectName: (creative.projects as any)?.name
      };
    } catch (error) {
      console.error('Get creative error:', error);
      throw error;
    }
  }

  /**
   * List creatives for a project
   */
  static async getProjectCreatives(
    projectId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<CreativeWithAnalysis[]> {
    try {
      const { data: creatives, error } = await supabase
        .from('creatives')
        .select(`
          *,
          projects!inner(name)
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      return (creatives || []).map(creative => ({
        ...creative,
        analysis: creative.analysis_data as unknown as AnalysisResult | undefined,
        projectName: (creative.projects as any)?.name
      }));
    } catch (error) {
      console.error('Get project creatives error:', error);
      throw error;
    }
  }

  /**
   * List all creatives for the current user
   */
  static async getUserCreatives(
    limit: number = 50,
    offset: number = 0
  ): Promise<CreativeWithAnalysis[]> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: creatives, error } = await supabase
        .from('creatives')
        .select(`
          *,
          projects(name)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      return (creatives || []).map(creative => ({
        ...creative,
        analysis: creative.analysis_data as unknown as AnalysisResult | undefined,
        projectName: (creative.projects as any)?.name
      }));
    } catch (error) {
      console.error('Get user creatives error:', error);
      throw error;
    }
  }

  /**
   * Update creative metadata
   */
  static async updateCreative(
    creativeId: string,
    updates: Partial<Pick<Creative, 'name' | 'status'>>
  ): Promise<CreativeWithAnalysis> {
    try {
      const { data: creative, error } = await supabase
        .from('creatives')
        .update(updates)
        .eq('id', creativeId)
        .select(`
          *,
          projects(name)
        `)
        .single();

      if (error) {
        throw error;
      }

      return {
        ...creative,
        analysis: creative.analysis_data as unknown as AnalysisResult | undefined,
        projectName: (creative.projects as any)?.name
      };
    } catch (error) {
      console.error('Update creative error:', error);
      throw error;
    }
  }

  /**
   * Rename creative family (updates the family_name for all versions in the family)
   */
  static async renameCreativeFamily(
    rootCreativeId: string,
    newFamilyName: string
  ): Promise<CreativeWithAnalysis> {
    try {
      // First, get the root creative to determine the family
      const { data: rootCreative, error: fetchError } = await supabase
        .from('creatives')
        .select('id, parent_creative_id')
        .eq('id', rootCreativeId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      // Determine the actual root ID (in case we were passed a version)
      const actualRootId = rootCreative.parent_creative_id || rootCreative.id;

      // Update family_name for the root creative and all its versions
      const { error: updateError } = await supabase
        .from('creatives')
        .update({ family_name: newFamilyName })
        .or(`id.eq.${actualRootId},parent_creative_id.eq.${actualRootId}`);

      if (updateError) {
        throw updateError;
      }

      // Return the updated root creative
      const { data: updatedCreative, error: selectError } = await supabase
        .from('creatives')
        .select(`
          *,
          projects(name)
        `)
        .eq('id', actualRootId)
        .single();

      if (selectError) {
        throw selectError;
      }

      return {
        ...updatedCreative,
        analysis: updatedCreative.analysis_data as unknown as AnalysisResult | undefined,
        projectName: (updatedCreative.projects as any)?.name
      };
    } catch (error) {
      console.error('Rename creative family error:', error);
      throw error;
    }
  }

  /**
   * Delete creative and associated files
   */
  static async deleteCreative(creativeId: string): Promise<void> {
    try {
      // Get creative data including project info for activity logging and storage tracking
      const { data: creative, error: fetchError } = await supabase
        .from('creatives')
        .select('url, project_id, name, file_size, user_id')
        .eq('id', creativeId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      // Delete analyses first (foreign key constraint)
      await supabase
        .from('analyses')
        .delete()
        .eq('creative_id', creativeId);

      // Delete creative record
      const { error: deleteError } = await supabase
        .from('creatives')
        .delete()
        .eq('id', creativeId);

      if (deleteError) {
        throw deleteError;
      }

      // Log creative deletion activity
      if (creative.project_id && creative.name) {
        try {
          await ActivityService.logCreativeDeleted(
            creative.project_id,
            creativeId,
            creative.name
          );
          console.log('✅ Activity logged: Creative deleted');
        } catch (activityError) {
          console.warn('⚠️ Failed to log deletion activity:', activityError);
          // Don't fail the entire operation if activity logging fails
        }
      }

      // Extract file path from URL and delete from storage
      if (creative.url) {
        try {
          const url = new URL(creative.url);
          const pathParts = url.pathname.split('/');
          // Supabase storage URLs have format: /storage/v1/object/public/bucket/path
          if (pathParts.length >= 6 && pathParts[4] === 'creatives') {
            const filePath = pathParts.slice(5).join('/');
            await StorageService.deleteCreative(filePath);
          }
        } catch (storageError) {
          console.warn('Failed to delete file from storage:', storageError);
          // Don't fail the operation if file deletion fails
        }
      }

      // Record storage reduction if we have file size and user ID
      if (creative.file_size && creative.user_id) {
        try {
          await StorageLimitService.recordFileDeletion(creative.user_id, creative.file_size);
          console.log('✅ Storage tracking updated: file deletion recorded');
        } catch (storageTrackingError) {
          console.warn('⚠️ Failed to update storage tracking:', storageTrackingError);
          // Don't fail the operation if storage tracking fails
        }
      }
    } catch (error) {
      console.error('Delete creative error:', error);
      throw error;
    }
  }

  /**
   * Reanalyze creative with new settings (now always analyzes all platforms)
   */
  static async reanalyzeCreative(
    creativeId: string,
    platforms?: string[], // Now optional as we analyze all platforms by default
    campaignType?: string,
    industry?: string,
    onProgress?: (progress: CreativeProgress) => void
  ): Promise<AnalysisResult> {
    try {
      // Get creative data
      const creative = await this.getCreative(creativeId);
      if (!creative) {
        throw new Error('Creative not found');
      }

      onProgress?.({
        stage: 'analyzing',
        progress: 10,
        message: 'Starting AI analysis...'
      });

      // Perform analysis (automatically analyzes all platforms)
      const analysisRequest: AnalysisRequest = {
        creativeUrl: creative.url,
        fileName: creative.name,
        creativeType: creative.type as 'image' | 'video',
        mimeType: creative.file_type || undefined,
        campaignType,
        industry
      };

      const analysisResult = await AIAnalysisService.analyzeCreative(analysisRequest);

      onProgress?.({
        stage: 'analyzing',
        progress: 80,
        message: 'Analysis completed, saving results...'
      });

      // Deduct credits for the re-analysis
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated for credit deduction');
      }

      const creditConsumed = await LimitEnforcementService.consumeAnalysisCredit(user.id, analysisResult.analysisId);
      if (!creditConsumed) {
        console.warn('⚠️ Failed to deduct credits for re-analysis, but continuing with analysis result');
        // Don't throw error to avoid breaking the re-analysis flow
      } else {
        console.log('✅ Credits deducted successfully for re-analysis');
      }

      // Analysis is automatically saved in analyzeCreative method
      // No need to call saveAnalysisResult here as it's private and already handled

      onProgress?.({
        stage: 'completed',
        progress: 100,
        message: 'Analysis updated successfully!'
      });

      // Send email notification for re-analysis completion
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: userData } = await supabase
            .from('users')
            .select('name, email')
            .eq('id', user.id)
            .single();
          
          if (userData && userData.email) {
            // Use the correct overall scores from analysis result
            const complianceScore = Math.round(analysisResult.overallScore || 0);
            const performanceScore = Math.round(analysisResult.performanceAnalysis?.overallPerformanceScore || 0);
            
            // Use the real analysis ID from the analysis result
            const analysisId = analysisResult.analysisId || `analysis-${Date.now()}`;
            
            await EmailService.send({
              to: userData.email,
              template: 'analysis-complete',
              data: {
                userName: userData.name || 'there',
                creativeName: creative.name,
                complianceScore: complianceScore,
                performanceScore: performanceScore,
                analysisId: analysisId
              },
              userId: user.id
            });
            
            console.log('✅ Re-analysis complete email sent successfully');
          }
        }
      } catch (emailError) {
        console.warn('⚠️ Failed to send re-analysis complete email:', emailError);
        // Don't fail the operation if email fails
      }

      return analysisResult;
    } catch (error) {
      console.error('Reanalyze creative error:', error);
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: 'Analysis failed',
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Get creative analysis status
   */
  static async getAnalysisStatus(creativeId: string): Promise<{
    hasAnalysis: boolean;
    status: string;
    platforms: string[];
    lastAnalyzed?: string;
  }> {
    try {
      const { data: analyses, error } = await supabase
        .from('analyses')
        .select('platforms, status, created_at')
        .eq('creative_id', creativeId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const hasAnalysis = (analyses?.length || 0) > 0;
      const platforms = analyses?.[0]?.platforms || [];
      const lastAnalyzed = analyses?.[0]?.created_at || undefined;
      const status = analyses?.every(a => a.status === 'approved') ? 'approved' : 
                    analyses?.some(a => a.status === 'rejected') ? 'rejected' : 'needs_review';

      return {
        hasAnalysis,
        status,
        platforms,
        lastAnalyzed
      };
    } catch (error) {
      console.error('Get analysis status error:', error);
      throw error;
    }
  }

  /**
   * Get creative performance insights
   */
  static async getCreativeInsights(creativeId: string): Promise<{
    overallScore: number;
    bestPlatform: string;
    worstPlatform: string;
    criticalIssues: number;
    recommendations: string[];
  } | null> {
    try {
      const creative = await this.getCreative(creativeId);
      if (!creative?.analysis) {
        return null;
      }

      const insights = AIAnalysisService.extractKeyInsights(creative.analysis);
      
      return {
        overallScore: creative.analysis.overallScore,
        bestPlatform: insights.topPlatform,
        worstPlatform: this.getWorstPlatform(creative.analysis),
        criticalIssues: insights.criticalIssues,
        recommendations: insights.improvements
      };
    } catch (error) {
      console.error('Get creative insights error:', error);
      throw error;
    }
  }

  /**
   * Find worst performing platform
   */
  private static getWorstPlatform(analysis: AnalysisResult): string {
    let worstPlatform = '';
    let lowestScore = 100;
    
    for (const [platform, platformAnalysis] of Object.entries(analysis.platforms)) {
      const totalScore = (platformAnalysis.complianceScore + platformAnalysis.performanceScore) / 2;
      if (totalScore < lowestScore) {
        lowestScore = totalScore;
        worstPlatform = platform;
      }
    }
    
    return worstPlatform;
  }

  /**
   * Search creatives by name or description
   */
  static async searchCreatives(
    query: string,
    projectId?: string,
    limit: number = 20
  ): Promise<CreativeWithAnalysis[]> {
    try {
      let queryBuilder = supabase
        .from('creatives')
        .select(`
          *,
          projects(name)
        `)
        .ilike('name', `%${query}%`)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (projectId) {
        queryBuilder = queryBuilder.eq('project_id', projectId);
      }

      const { data: creatives, error } = await queryBuilder;

      if (error) {
        throw error;
      }

      return (creatives || []).map(creative => ({
        ...creative,
        analysis: creative.analysis_data as unknown as AnalysisResult | undefined,
        projectName: (creative.projects as any)?.name
      }));
    } catch (error) {
      console.error('Search creatives error:', error);
      throw error;
    }
  }
}

export default CreativeService; 